import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Calendar, CheckCircle, AlertTriangle, Settings, Shield, Trash2,
  RefreshCw, Clock,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import SEO from '../../components/SEO';

/* Reusable styled browser frame (self-contained to this page) */
const MockFrame = ({ url, children }) => (
  <div className="mt-8 rounded-xl overflow-hidden border border-gray-200 shadow-md bg-white">
    <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-4 py-2.5 flex items-center gap-3">
      <div className="flex gap-1.5">
        <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
      </div>
      <div className="flex-1 bg-slate-600/60 rounded px-3 py-1 text-[11px] text-slate-200 font-mono truncate">
        {url}
      </div>
    </div>
    <div className="bg-slate-50">{children}</div>
  </div>
);

const GoogleCalendarDocPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Google Calendar Integration - MoveMate Documentation"
        description="Connect Google Calendar to MoveMate and auto-sync every moving job to your team's calendar. Full setup, what gets synced, privacy details, and troubleshooting."
        url="https://movemate.tech/docs/google-calendar"
      />

      {/* Header */}
      <header className="bg-gradient-to-r from-primary to-cyan-500 text-white py-10 px-4" data-testid="gcal-header">
        <div className="max-w-4xl mx-auto">
          <Link to="/docs" className="inline-flex items-center text-white/80 hover:text-white mb-4" data-testid="back-to-docs-link">
            <ArrowLeft size={20} className="mr-2" />
            Back to Documentation
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <Calendar size={32} />
            <span className="text-sm bg-white/20 px-3 py-1 rounded-full">Integrations</span>
            <span className="text-sm bg-white/10 px-3 py-1 rounded-full flex items-center gap-1.5"><Clock size={14} /> 5 min read</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold">Google Calendar Integration</h1>
          <p className="text-cyan-100 mt-3 max-w-2xl">
            Every job scheduled in MoveMate automatically shows up in your Google Calendar — complete with addresses, team assignments, and job status.
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto py-12 px-4">

        {/* Overview */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-8" data-testid="overview-section">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Overview</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            MoveMate integrates with Google Calendar to automatically sync your moving jobs. When you create, update, or complete a job,
            it's reflected in your Google Calendar — helping you and your team stay organized without any manual data entry.
          </p>

          <div className="grid md:grid-cols-3 gap-4 mt-6">
            <div className="bg-green-50 p-4 rounded-lg">
              <CheckCircle className="text-green-600 mb-2" size={24} />
              <h4 className="font-semibold text-gray-900">Auto-Sync</h4>
              <p className="text-sm text-gray-600">Jobs sync automatically when created or updated</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <RefreshCw className="text-blue-600 mb-2" size={24} />
              <h4 className="font-semibold text-gray-900">Live Updates</h4>
              <p className="text-sm text-gray-600">Changes in MoveMate update your calendar within seconds</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <Shield className="text-purple-600 mb-2" size={24} />
              <h4 className="font-semibold text-gray-900">Secure</h4>
              <p className="text-sm text-gray-600">Minimum-scope OAuth, encrypted token storage</p>
            </div>
          </div>
        </div>

        {/* How to Connect */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-8" data-testid="how-to-connect">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Settings className="text-primary" size={24} />
            How to Connect Google Calendar
          </h2>

          <div className="space-y-6">
            {[
              { n: 1, t: 'Open Company Settings', d: <>Navigate to <strong>Settings → Company Settings</strong> in your MoveMate dashboard.</> },
              { n: 2, t: 'Find the Google Calendar section', d: <>Scroll down to the <strong>Integrations</strong> section and find Google Calendar.</> },
              { n: 3, t: 'Click Connect', d: <>Click the <strong>Connect Google Calendar</strong> button.</> },
              { n: 4, t: 'Sign in to Google', d: 'A Google sign-in window will appear. Sign in with the Google account that owns the calendar you want to use.' },
              { n: 5, t: 'Grant Permission', d: <>Click <strong>Allow</strong> to grant MoveMate permission to create and manage calendar events.</> },
            ].map(s => (
              <div className="flex gap-4" key={s.n}>
                <div className="w-8 h-8 bg-gradient-to-br from-primary to-cyan-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">{s.n}</div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">{s.t}</h4>
                  <p className="text-gray-600">{s.d}</p>
                </div>
              </div>
            ))}
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle size={18} />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Done!</h4>
                <p className="text-gray-600">Your Google Calendar is now connected. Existing jobs back-fill within a minute; new ones sync instantly.</p>
              </div>
            </div>
          </div>

          {/* Mockup: Connection panel */}
          <MockFrame url="https://admin.movemate.tech/settings/company#integrations">
            <div className="p-6">
              <h4 className="font-semibold text-gray-900 mb-1">Integrations</h4>
              <p className="text-xs text-gray-500 mb-4">Connect external tools to supercharge your workflow</p>
              <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-between bg-white">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Calendar className="text-blue-600" size={20} />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 text-sm">Google Calendar</div>
                    <div className="text-xs text-gray-500">Sync jobs to your Google account</div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-primary to-cyan-500 text-white px-4 py-1.5 rounded text-xs font-semibold">
                  Connect Google Calendar
                </div>
              </div>
            </div>
          </MockFrame>
        </div>

        {/* What Gets Synced */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-8" data-testid="what-gets-synced">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">What Gets Synced to Your Calendar</h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <CheckCircle className="text-green-500" size={18} />
                Included in Calendar Event
              </h4>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li>• Job reference number</li>
                <li>• Customer name</li>
                <li>• Move date and time</li>
                <li>• Pickup address</li>
                <li>• Delivery address</li>
                <li>• Estimated duration</li>
                <li>• Assigned team members</li>
                <li>• Job status</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <AlertTriangle className="text-amber-500" size={18} />
                NOT Synced (For Privacy)
              </h4>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li>• Customer phone number</li>
                <li>• Customer email</li>
                <li>• Payment information</li>
                <li>• Detailed inventory list</li>
                <li>• Internal notes</li>
                <li>• Pricing details</li>
              </ul>
            </div>
          </div>

          {/* Mockup: Example synced calendar event */}
          <MockFrame url="https://calendar.google.com/calendar/u/0/r/eventedit">
            <div className="p-6 max-w-xl mx-auto bg-white border border-gray-200 rounded-lg shadow-sm my-4">
              <div className="flex items-start gap-3">
                <div className="w-3 h-3 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 text-[15px]">JOB-2026-042 — Emma Thompson</div>
                  <div className="text-xs text-gray-500 mt-0.5">Saturday, 14 March 2026 · 09:00 – 15:00 (GMT)</div>

                  <div className="mt-3 space-y-1.5 text-xs">
                    <div className="flex gap-2">
                      <span className="text-gray-400 w-16">From</span>
                      <span className="text-gray-700">42 Camden Lock, London NW1</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-gray-400 w-16">To</span>
                      <span className="text-gray-700">7 Brighton Marina, Brighton BN2</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-gray-400 w-16">Team</span>
                      <span className="text-gray-700">Mike Hughes (Driver), Priya Patel (Porter)</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-gray-400 w-16">Status</span>
                      <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">Scheduled</span>
                    </div>
                  </div>
                  <div className="mt-3 text-[11px] text-gray-400 border-t border-gray-100 pt-2">
                    Synced from MoveMate · admin.movemate.tech/jobs/JOB-2026-042
                  </div>
                </div>
              </div>
            </div>
          </MockFrame>
        </div>

        {/* Managing Connection */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-8" data-testid="managing-connection">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Managing Your Connection</h2>

          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <RefreshCw className="text-blue-500" size={20} />
                Refreshing the Connection
              </h4>
              <p className="text-gray-600">
                If you experience sync issues, refresh the connection from Company Settings.
                Click <strong>Refresh Connection</strong> to re-authenticate without losing existing synced events.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Trash2 className="text-red-500" size={20} />
                Disconnecting Google Calendar
              </h4>
              <p className="text-gray-600 mb-3">To disconnect Google Calendar:</p>
              <ol className="list-decimal list-inside text-gray-600 space-y-1 ml-4">
                <li>Go to <strong>Settings → Company Settings</strong></li>
                <li>Find the Google Calendar section</li>
                <li>Click <strong>Disconnect</strong></li>
                <li>Confirm the disconnection</li>
              </ol>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                <p className="text-amber-800 text-sm">
                  <strong>Note:</strong> Disconnecting will stop future syncing but won't delete existing calendar events.
                  You'll need to manually remove those from Google Calendar if desired.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Privacy & Security */}
        <div className="bg-blue-50 rounded-xl p-8 mb-8 border border-blue-200" data-testid="privacy-section">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="text-blue-600" size={24} />
            Privacy &amp; Security
          </h2>

          <div className="space-y-4 text-gray-700">
            <p>MoveMate takes your privacy seriously. Here's how we handle your Google Calendar data:</p>
            <ul className="space-y-2">
              {[
                'We only access calendars you explicitly authorize',
                'We only create, read, and update MoveMate job events',
                'We do NOT read your personal calendar events',
                'We do NOT share your calendar data with third parties',
                'Authentication tokens are encrypted and stored securely',
                'You can revoke access at any time from Google Account → Security → Third-party access',
              ].map(txt => (
                <li key={txt} className="flex items-start gap-2">
                  <CheckCircle className="text-green-500 mt-1 flex-shrink-0" size={18} />
                  <span>{txt}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-sm">
              For full details, see our <Link to="/privacy" className="text-primary hover:underline font-medium">Privacy Policy</Link>.
            </p>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="bg-white rounded-xl shadow-sm p-8 mb-8" data-testid="troubleshooting-section">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Troubleshooting</h2>

          <div className="space-y-6">
            <Issue q="Events not appearing in calendar">
              <li>Check that the job has a scheduled date <em>and</em> time (not just a date)</li>
              <li>Verify the calendar connection is active in Settings</li>
              <li>Try refreshing the connection</li>
              <li>Allow 1–2 minutes for initial back-fill of existing jobs</li>
            </Issue>
            <Issue q="Connection keeps disconnecting">
              <li>Re-authenticate by clicking Connect again</li>
              <li>Check that you haven't revoked access at myaccount.google.com → Security</li>
              <li>Ensure your Google account is in good standing (not locked/suspended)</li>
            </Issue>
            <Issue q="Wrong calendar being used">
              <li>MoveMate always syncs to the <strong>primary</strong> calendar of the connected Google account</li>
              <li>To use a different calendar, disconnect and reconnect with the correct account</li>
            </Issue>
            <Issue q="Events are duplicating in my calendar">
              <li>Happens when the same Google account is connected twice with different permissions</li>
              <li>Fix: Disconnect, clear events, reconnect once</li>
            </Issue>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4" data-testid="bottom-nav">
          <Link to="/docs/quick-start" className="text-primary hover:underline flex items-center gap-2 font-medium">
            <ArrowLeft size={20} />
            Quick Start Guide
          </Link>
          <Link to="/contact">
            <Button variant="outline" data-testid="contact-support-btn">Need Help? Contact Support</Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-white py-8 px-4 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-sm text-gray-600">
            <Link to="/privacy" className="hover:text-primary">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-primary">Terms &amp; Conditions</Link>
            <Link to="/contact" className="hover:text-primary">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

const Issue = ({ q, children }) => (
  <div>
    <h4 className="font-semibold text-gray-900 mb-2">{q}</h4>
    <ul className="text-gray-600 space-y-1 ml-4 text-sm list-disc list-inside">{children}</ul>
  </div>
);

export default GoogleCalendarDocPage;
